import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useBaseUrl } from '../../../utils/utils';
import * as localforage from 'localforage';
import Swal from 'sweetalert2';

interface Guideline {
    id: string;
    title: string;
    guideline_type: string;
    compliance_level: string;
    summary: string;
    version: string;
    effective_date: string;
    review_date: string;
    target_audience: string;
    created_by_name: string;
    approved_by_name: string;
    is_active: boolean;
}

interface Props {
    onBack: () => void;
}

const GuidelinesCenter: React.FC<Props> = ({ onBack }) => {
    const baseUrl = useBaseUrl();
    const [guidelines, setGuidelines] = useState<Guideline[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedType, setSelectedType] = useState<string>('all');
    const [selectedCompliance, setSelectedCompliance] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(12);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [showSuggestModal, setShowSuggestModal] = useState<boolean>(false);

    const guidelineTypes = [
        { value: 'clinical', label: 'Clinical Guideline', color: '#007bff', icon: 'bx-health' },
        { value: 'protocol', label: 'Medical Protocol', color: '#28a745', icon: 'bx-clipboard' },
        { value: 'procedure', label: 'Standard Procedure', color: '#17a2b8', icon: 'bx-cog' },
        { value: 'policy', label: 'Policy Guideline', color: '#6610f2', icon: 'bx-shield' },
        { value: 'safety', label: 'Safety Protocol', color: '#fd7e14', icon: 'bx-shield-alt' },
        { value: 'emergency', label: 'Emergency Procedure', color: '#dc3545', icon: 'bx-error' }
    ];

    const complianceLevels = [
        { value: 'mandatory', label: 'Mandatory', color: '#dc3545' },
        { value: 'recommended', label: 'Recommended', color: '#28a745' },
        { value: 'optional', label: 'Optional', color: '#6c757d' },
        { value: 'deprecated', label: 'Deprecated', color: '#ffc107' }
    ];

    useEffect(() => {
        loadGuidelines();
    }, [selectedType, selectedCompliance]);

    const loadGuidelines = async () => {
        setLoading(true);
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            let url = `${baseUrl}/resources/api/v1/guidelines/`;
            const params = new URLSearchParams();
            
            if (selectedType !== 'all') params.append('guideline_type', selectedType);
            if (selectedCompliance !== 'all') params.append('compliance_level', selectedCompliance);

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axios.get(url, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data) {
                if (Array.isArray(response.data.results)) {
                    // Paginated response
                    setGuidelines(currentPage === 1 ? response.data.results : [...guidelines, ...response.data.results]);
                    setTotalCount(response.data.count || 0);
                    setHasMore(!!response.data.next);
                } else if (Array.isArray(response.data)) {
                    // Simple array response
                    setGuidelines(response.data);
                    setTotalCount(response.data.length);
                    setHasMore(false);
                }
            }
        } catch (error) {
            console.error('Error loading guidelines:', error);
            setGuidelines([]); // No hardcoded data
        } finally {
            setLoading(false);
        }
    };

    const loadMoreGuidelines = async () => {
        if (!hasMore || loading) return;
        
        setCurrentPage(prev => prev + 1);
        await loadGuidelines();
    };

    const handleScroll = () => {
        if (window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight) return;
        loadMoreGuidelines();
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [hasMore, loading]);

    const getTypeInfo = (type: string) => {
        return guidelineTypes.find(t => t.value === type) || guidelineTypes[0];
    };

    const getComplianceInfo = (level: string) => {
        return complianceLevels.find(c => c.value === level) || complianceLevels[1];
    };

    const filteredGuidelines = guidelines.filter(guideline =>
        guideline.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guideline.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guideline.target_audience.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const viewGuideline = (guideline: Guideline) => {
        Swal.fire({
            title: guideline.title,
            html: `
                <div class="text-start">
                    <p><strong>Version:</strong> ${guideline.version}</p>
                    <p><strong>Type:</strong> ${getTypeInfo(guideline.guideline_type).label}</p>
                    <p><strong>Compliance:</strong> ${getComplianceInfo(guideline.compliance_level).label}</p>
                    <p><strong>Target Audience:</strong> ${guideline.target_audience}</p>
                    <p><strong>Effective Date:</strong> ${formatDate(guideline.effective_date)}</p>
                    <p><strong>Review Date:</strong> ${formatDate(guideline.review_date)}</p>
                    <hr>
                    <p>${guideline.summary}</p>
                </div>
            `,
            width: '600px',
            showCancelButton: true,
            confirmButtonText: 'View Full Document',
            cancelButtonText: 'Close'
        }).then((result) => {
            if (result.isConfirmed) {
                // Implement full document viewing
                Swal.fire('Info', 'Full document viewing will be implemented', 'info');
            }
        });
    };

    return (
        <div className="container-fluid p-4">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-1">
                                <i className="bx bx-shield-quarter me-2 text-primary"></i>
                                Clinical Guidelines & Protocols
                            </h4>
                            <p className="text-muted mb-0">Access medical guidelines, protocols, and standard procedures</p>
                        </div>
                        <div className="d-flex gap-2">
                            <button 
                                className="btn btn-primary"
                                onClick={() => setShowSuggestModal(true)}
                            >
                                <i className="bx bx-plus me-2"></i>
                                Suggest Guideline
                            </button>
                            <button 
                                className="btn btn-outline-secondary"
                                onClick={onBack}
                            >
                                <i className="bx bx-arrow-back me-2"></i>
                                Back to Resources
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <div className="row g-3 align-items-end">
                                <div className="col-md-6">
                                    <label className="form-label small text-muted">Search Guidelines</label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <i className="bx bx-search"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search by title, content, or audience..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small text-muted">Type</label>
                                    <select
                                        className="form-select"
                                        value={selectedType}
                                        onChange={(e) => setSelectedType(e.target.value)}
                                    >
                                        <option value="all">All Types</option>
                                        {guidelineTypes.map((type) => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small text-muted">Compliance Level</label>
                                    <select
                                        className="form-select"
                                        value={selectedCompliance}
                                        onChange={(e) => setSelectedCompliance(e.target.value)}
                                    >
                                        <option value="all">All Levels</option>
                                        {complianceLevels.map((level) => (
                                            <option key={level.value} value={level.value}>
                                                {level.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Guidelines Grid */}
            <div className="row">
                <div className="col-12">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading guidelines...</p>
                        </div>
                    ) : filteredGuidelines.length > 0 ? (
                        <div className="row g-4">
                            {filteredGuidelines.map((guideline) => {
                                const typeInfo = getTypeInfo(guideline.guideline_type);
                                const complianceInfo = getComplianceInfo(guideline.compliance_level);
                                
                                return (
                                    <div key={guideline.id} className="col-lg-6 col-xl-4">
                                        <div className="card h-100 border-0 shadow-sm hover-shadow">
                                            <div className="card-body">
                                                <div className="d-flex align-items-start mb-3">
                                                    <div 
                                                        className="avatar-sm rounded flex-shrink-0 d-flex align-items-center justify-content-center me-3"
                                                        style={{ backgroundColor: `${typeInfo.color}15` }}
                                                    >
                                                        <i className={`bx ${typeInfo.icon}`} style={{ color: typeInfo.color, fontSize: '24px' }}></i>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <h6 className="mb-2 line-clamp-2">{guideline.title}</h6>
                                                        <div className="d-flex flex-wrap gap-1 mb-2">
                                                            <span 
                                                                className="badge"
                                                                style={{ backgroundColor: typeInfo.color, color: 'white' }}
                                                            >
                                                                {typeInfo.label}
                                                            </span>
                                                            <span 
                                                                className="badge"
                                                                style={{ backgroundColor: complianceInfo.color, color: 'white' }}
                                                            >
                                                                {complianceInfo.label}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <p className="text-muted small mb-3 line-clamp-3">{guideline.summary}</p>
                                                
                                                <div className="row g-2 mb-3 small">
                                                    <div className="col-6">
                                                        <div className="text-muted">Version</div>
                                                        <div className="fw-medium">{guideline.version}</div>
                                                    </div>
                                                    <div className="col-6">
                                                        <div className="text-muted">Target</div>
                                                        <div className="fw-medium text-truncate">{guideline.target_audience}</div>
                                                    </div>
                                                </div>
                                                
                                                <div className="row g-2 mb-3 small">
                                                    <div className="col-6">
                                                        <div className="text-muted">Effective</div>
                                                        <div className="fw-medium">{formatDate(guideline.effective_date)}</div>
                                                    </div>
                                                    <div className="col-6">
                                                        <div className="text-muted">Review</div>
                                                        <div className="fw-medium">{formatDate(guideline.review_date)}</div>
                                                    </div>
                                                </div>
                                                
                                                <div className="small text-muted mb-3">
                                                    <div>Created by: {guideline.created_by_name}</div>
                                                    {guideline.approved_by_name && (
                                                        <div>Approved by: {guideline.approved_by_name}</div>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="card-footer bg-transparent border-0 pt-0">
                                                <div className="d-grid">
                                                    <button
                                                        className="btn btn-outline-primary"
                                                        onClick={() => viewGuideline(guideline)}
                                                    >
                                                        <i className="bx bx-show me-2"></i>
                                                        View Guideline
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <i className="bx bx-shield-quarter display-1 text-muted"></i>
                            <h5 className="mt-3 text-muted">No Guidelines Found</h5>
                            <p className="text-muted">
                                {searchTerm 
                                    ? `No guidelines match your search criteria`
                                    : `No clinical guidelines are currently available.`
                                }
                            </p>
                            {searchTerm && (
                                <button 
                                    className="btn btn-outline-primary"
                                    onClick={() => setSearchTerm('')}
                                >
                                    Clear Search
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Infinite Scroll Loading Indicator */}
            {loading && guidelines.length > 0 && (
                <div className="row mt-4">
                    <div className="col-12 text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading more...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading more guidelines...</p>
                    </div>
                </div>
            )}

            {/* Guidelines Count */}
            {guidelines.length > 0 && (
                <div className="row mt-4">
                    <div className="col-12 text-center text-muted">
                        Showing {filteredGuidelines.length} of {totalCount} guidelines
                        {hasMore && !loading && (
                            <div className="mt-2">
                                <small>Scroll down to load more</small>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <style>{`
                .line-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                
                .line-clamp-3 {
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                
                .hover-shadow {
                    transition: box-shadow 0.15s ease-in-out;
                }
                
                .hover-shadow:hover {
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
                }
                
                .avatar-sm {
                    width: 3rem;
                    height: 3rem;
                }
            `}</style>

            {/* Suggest Guideline Modal */}
            {showSuggestModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <SuggestGuidelineModal 
                                onClose={() => setShowSuggestModal(false)}
                                onSuccess={() => {
                                    setShowSuggestModal(false);
                                    loadGuidelines(); // Reload guidelines after successful submission
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Suggest Guideline Modal Component
interface SuggestGuidelineModalProps {
    onClose: () => void;
    onSuccess: () => void;
}

const SuggestGuidelineModal: React.FC<SuggestGuidelineModalProps> = ({ onClose, onSuccess }) => {
    const baseUrl = useBaseUrl();
    const [step, setStep] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(false);
    
    // Form data states
    const [formData, setFormData] = useState({
        // Professional verification
        professionalTitle: '',
        professionalCapacity: '',
        yearsOfExperience: '',
        specialization: '',
        
        // Basic guideline info
        title: '',
        guidelineType: 'clinical',
        complianceLevel: 'recommended',
        targetAudience: '',
        summary: '',
        
        // Content
        contentType: 'text',
        content: '',
        references: '',
        
        // File upload
        pdfFile: null as File | null
    });

    const guidelineTypes = [
        { value: 'clinical', label: 'Clinical Guideline' },
        { value: 'protocol', label: 'Medical Protocol' },
        { value: 'procedure', label: 'Standard Procedure' },
        { value: 'policy', label: 'Policy Guideline' },
        { value: 'safety', label: 'Safety Protocol' },
        { value: 'emergency', label: 'Emergency Procedure' }
    ];

    const complianceLevels = [
        { value: 'mandatory', label: 'Mandatory' },
        { value: 'recommended', label: 'Recommended' },
        { value: 'optional', label: 'Optional' }
    ];

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setFormData(prev => ({
            ...prev,
            pdfFile: file
        }));
    };

    const validateStep = (stepNumber: number): boolean => {
        switch (stepNumber) {
            case 1:
                return !!(formData.professionalTitle && formData.professionalCapacity && 
                         formData.yearsOfExperience && formData.specialization);
            case 2:
                return !!(formData.title && formData.summary && formData.targetAudience);
            case 3:
                if (formData.contentType === 'text') {
                    return !!formData.content;
                } else {
                    return !!formData.pdfFile;
                }
            default:
                return true;
        }
    };

    const nextStep = () => {
        if (validateStep(step)) {
            setStep(prev => prev + 1);
        } else {
            Swal.fire('Error', 'Please fill in all required fields', 'error');
        }
    };

    const prevStep = () => {
        setStep(prev => prev - 1);
    };

    const submitSuggestion = async () => {
        if (!validateStep(3)) {
            Swal.fire('Error', 'Please complete all required fields', 'error');
            return;
        }

        setLoading(true);
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            // Prepare form data for submission
            const submitData = new FormData();
            
            // Add all form fields
            submitData.append('title', formData.title);
            submitData.append('guideline_type', formData.guidelineType);
            submitData.append('compliance_level', formData.complianceLevel);
            submitData.append('professional_title', formData.professionalTitle);
            submitData.append('professional_capacity', formData.professionalCapacity);
            submitData.append('years_of_experience', formData.yearsOfExperience);
            submitData.append('specialization', formData.specialization);
            submitData.append('content_type', formData.contentType);
            submitData.append('summary', formData.summary);
            submitData.append('target_audience', formData.targetAudience);
            submitData.append('references', formData.references);
            
            if (formData.contentType === 'text') {
                submitData.append('content', formData.content);
            } else if (formData.pdfFile) {
                submitData.append('pdf_file', formData.pdfFile);
            }

            const response = await axios.post(
                `${baseUrl}/resources/api/v1/guideline-suggestions/`,
                submitData,
                {
                    headers: {
                        'X-Clinic-ID': clinicId || '',
                        'X-Staff-ID': staffId || '',
                    }
                }
            );

            if (response.status === 201) {
                await Swal.fire({
                    title: 'Success!',
                    text: 'Your guideline suggestion has been submitted for review. You will be notified once it has been reviewed by the administrators.',
                    icon: 'success',
                    confirmButtonText: 'OK'
                });
                onSuccess();
            }
        } catch (error: any) {
            console.error('Error submitting guideline suggestion:', error);
            Swal.fire(
                'Error',
                error.response?.data?.message || 'Failed to submit guideline suggestion. Please try again.',
                'error'
            );
        } finally {
            setLoading(false);
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div>
                        <h5 className="mb-3">Professional Verification</h5>
                        <p className="text-muted mb-4">Please verify your professional credentials to ensure the quality of suggested guidelines.</p>
                        
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Professional Title <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g., MD, RN, PhD"
                                    value={formData.professionalTitle}
                                    onChange={(e) => handleInputChange('professionalTitle', e.target.value)}
                                />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Years of Experience <span className="text-danger">*</span></label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Years"
                                    min="0"
                                    value={formData.yearsOfExperience}
                                    onChange={(e) => handleInputChange('yearsOfExperience', e.target.value)}
                                />
                            </div>
                        </div>
                        
                        <div className="mb-3">
                            <label className="form-label">Professional Capacity <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g., Senior Cardiologist, ICU Nurse Manager"
                                value={formData.professionalCapacity}
                                onChange={(e) => handleInputChange('professionalCapacity', e.target.value)}
                            />
                        </div>
                        
                        <div className="mb-3">
                            <label className="form-label">Specialization <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g., Cardiology, Emergency Medicine"
                                value={formData.specialization}
                                onChange={(e) => handleInputChange('specialization', e.target.value)}
                            />
                        </div>
                    </div>
                );
            
            case 2:
                return (
                    <div>
                        <h5 className="mb-3">Guideline Information</h5>
                        <p className="text-muted mb-4">Provide basic information about the guideline you want to suggest.</p>
                        
                        <div className="mb-3">
                            <label className="form-label">Guideline Title <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter a clear, descriptive title"
                                value={formData.title}
                                onChange={(e) => handleInputChange('title', e.target.value)}
                            />
                        </div>
                        
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Guideline Type</label>
                                <select
                                    className="form-select"
                                    value={formData.guidelineType}
                                    onChange={(e) => handleInputChange('guidelineType', e.target.value)}
                                >
                                    {guidelineTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Compliance Level</label>
                                <select
                                    className="form-select"
                                    value={formData.complianceLevel}
                                    onChange={(e) => handleInputChange('complianceLevel', e.target.value)}
                                >
                                    {complianceLevels.map(level => (
                                        <option key={level.value} value={level.value}>
                                            {level.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        
                        <div className="mb-3">
                            <label className="form-label">Target Audience <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g., All Staff, Emergency Personnel, ICU Staff"
                                value={formData.targetAudience}
                                onChange={(e) => handleInputChange('targetAudience', e.target.value)}
                            />
                        </div>
                        
                        <div className="mb-3">
                            <label className="form-label">Summary <span className="text-danger">*</span></label>
                            <textarea
                                className="form-control"
                                rows={3}
                                placeholder="Brief summary of the guideline's purpose and scope"
                                value={formData.summary}
                                onChange={(e) => handleInputChange('summary', e.target.value)}
                            />
                        </div>
                    </div>
                );
            
            case 3:
                return (
                    <div>
                        <h5 className="mb-3">Content Submission</h5>
                        <p className="text-muted mb-4">Provide the guideline content either by typing it directly or uploading a PDF file.</p>
                        
                        <div className="mb-3">
                            <label className="form-label">Content Type</label>
                            <div className="d-flex gap-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="contentType"
                                        value="text"
                                        checked={formData.contentType === 'text'}
                                        onChange={(e) => handleInputChange('contentType', e.target.value)}
                                    />
                                    <label className="form-check-label">Type Content</label>
                                </div>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="contentType"
                                        value="pdf"
                                        checked={formData.contentType === 'pdf'}
                                        onChange={(e) => handleInputChange('contentType', e.target.value)}
                                    />
                                    <label className="form-check-label">Upload PDF</label>
                                </div>
                            </div>
                        </div>
                        
                        {formData.contentType === 'text' ? (
                            <div className="mb-3">
                                <label className="form-label">Guideline Content <span className="text-danger">*</span></label>
                                <textarea
                                    className="form-control"
                                    rows={10}
                                    placeholder="Enter the complete guideline content here..."
                                    value={formData.content}
                                    onChange={(e) => handleInputChange('content', e.target.value)}
                                />
                                <div className="form-text">Use clear formatting and include all necessary details, steps, and procedures.</div>
                            </div>
                        ) : (
                            <div className="mb-3">
                                <label className="form-label">Upload PDF File <span className="text-danger">*</span></label>
                                <input
                                    type="file"
                                    className="form-control"
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                />
                                <div className="form-text">Upload a PDF file containing the guideline content. Text will be extracted automatically.</div>
                                {formData.pdfFile && (
                                    <div className="mt-2">
                                        <small className="text-success">
                                            <i className="bx bx-check me-1"></i>
                                            Selected: {formData.pdfFile.name}
                                        </small>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <div className="mb-3">
                            <label className="form-label">References (Optional)</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                placeholder="List any references, studies, or sources that support this guideline..."
                                value={formData.references}
                                onChange={(e) => handleInputChange('references', e.target.value)}
                            />
                        </div>
                    </div>
                );
            
            default:
                return null;
        }
    };

    return (
        <>
            <div className="modal-header">
                <h5 className="modal-title">
                    <i className="bx bx-plus-medical me-2"></i>
                    Suggest New Guideline
                </h5>
                <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            
            <div className="modal-body">
                {/* Progress Indicator */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                        <span className={`badge ${step >= 1 ? 'bg-primary' : 'bg-secondary'}`}>1. Verification</span>
                        <span className={`badge ${step >= 2 ? 'bg-primary' : 'bg-secondary'}`}>2. Information</span>
                        <span className={`badge ${step >= 3 ? 'bg-primary' : 'bg-secondary'}`}>3. Content</span>
                    </div>
                    <div className="progress" style={{ height: '4px' }}>
                        <div 
                            className="progress-bar bg-primary" 
                            style={{ width: `${(step / 3) * 100}%` }}
                        ></div>
                    </div>
                </div>

                {/* Step Content */}
                {renderStep()}
            </div>
            
            <div className="modal-footer">
                <div className="d-flex justify-content-between w-100">
                    <div>
                        {step > 1 && (
                            <button 
                                type="button" 
                                className="btn btn-outline-secondary"
                                onClick={prevStep}
                                disabled={loading}
                            >
                                <i className="bx bx-chevron-left me-1"></i>
                                Previous
                            </button>
                        )}
                    </div>
                    <div className="d-flex gap-2">
                        <button 
                            type="button" 
                            className="btn btn-secondary"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        {step < 3 ? (
                            <button 
                                type="button" 
                                className="btn btn-primary"
                                onClick={nextStep}
                                disabled={loading}
                            >
                                Next
                                <i className="bx bx-chevron-right ms-1"></i>
                            </button>
                        ) : (
                            <button 
                                type="button" 
                                className="btn btn-success"
                                onClick={submitSuggestion}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <i className="bx bx-check me-1"></i>
                                        Submit Suggestion
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default GuidelinesCenter;
